import ClientAppSidebar from "../../../../components/ClientAppSidebar";
import {
  YourActionsRequiredSection,
  BooksMonthByMonthStatusSection,
  RecentActivitySection,
  AccountantSummaryCardsSection,
  AccountantMonthDrilldownSection,
  WorkHistorySection,
} from "../components/books_health_components";
import { useBooksHealthPage } from "../hooks/useBooksHealthPage";
import { Button } from "../../../../ui/components/Button";
import { DropdownMenu } from "../../../../ui/components/DropdownMenu";
import * as SubframeCore from "@subframe/core";
import { FeatherChevronDown } from "@subframe/core";
import React from "react";

function formatSubtitle(lastUpdated?: string, lastUpdatedBy?: string): string {
  const todayFormatted = new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  if (lastUpdated && lastUpdatedBy) {
    return `${todayFormatted}`;
  }
  return todayFormatted;
}

export default function BooksHealthPage() {
  const {
    data,
    loading,
    error,
    actionRequiredLoading,
    actionRequiredError,
    monthByMonthLoading,
    monthByMonthError,
    monthlyModuleStatusError,
    recentActivityLoading,
    recentActivityError,
    accountantSummary,
    accountantSummaryLoading,
    accountantSummaryError,
    selectedYear,
    selectedMonth,
    summaryPeriod,
    onSummaryPeriodChange,
    onSummaryMonthChange,
    detailYear,
    detailMonth,
    detailPeriod,
    onDetailMonthChange,
    onDetailPeriodChange,
    billsDetail,
    billsDetailLoading,
    billsDetailError,
    billsOffset,
    setBillsOffset,
    billsLimit,
    invoicesDetail,
    invoicesDetailLoading,
    invoicesDetailError,
    invoicesOffset,
    setInvoicesOffset,
    invoicesLimit,
    transactionsDetail,
    transactionsDetailLoading,
    transactionsDetailError,
    workHistory,
    workHistoryLoading,
    workHistoryError,

    getMonthNumberFromAbbreviation,
  } = useBooksHealthPage();

  const subtitle = formatSubtitle(data?.lastUpdated, data?.lastUpdatedBy);

  const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <div className="flex w-full items-start h-screen">
      <ClientAppSidebar />
      <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 self-stretch bg-default-background overflow-y-auto">
        <div className="container max-w-none flex w-full grow shrink-0 basis-0 items-start gap-8 bg-neutral-50 py-12 overflow-auto">
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-8">
            <div className="flex w-full flex-col items-start gap-8 bg-neutral-50">
              <div className="flex w-full flex-col items-start gap-1">
                <span className="text-heading-1 font-heading-1 text-neutral-900">
                  Books Health
                </span>
                <span className="text-caption font-caption text-neutral-500">
                  {subtitle}
                </span>
              </div>

              {error && (
                <div className="rounded-lg border border-solid border-error-200 bg-error-50 px-4 py-3">
                  <span className="text-body font-body text-error-700">
                    {error}
                  </span>
                </div>
              )}

              {actionRequiredError && (
                <div className="rounded-lg border border-solid border-error-200 bg-error-50 px-4 py-3">
                  <span className="text-body font-body text-error-700">
                    {actionRequiredError}
                  </span>
                </div>
              )}

              {monthByMonthError && (
                <div className="rounded-lg border border-solid border-error-200 bg-error-50 px-4 py-3">
                  <span className="text-body font-body text-error-700">
                    {monthByMonthError}
                  </span>
                </div>
              )}

              {monthlyModuleStatusError && (
                <div className="rounded-lg border border-solid border-error-200 bg-error-50 px-4 py-3">
                  <span className="text-body font-body text-error-700">
                    {monthlyModuleStatusError}
                  </span>
                </div>
              )}

              {recentActivityError && (
                <div className="rounded-lg border border-solid border-error-200 bg-error-50 px-4 py-3">
                  <span className="text-body font-body text-error-700">
                    {recentActivityError}
                  </span>
                </div>
              )}

              <YourActionsRequiredSection
                items={data?.actionRequiredItems}
                loading={actionRequiredLoading}
              />

              <BooksMonthByMonthStatusSection
                months={data?.monthByMonthStatus}
                loading={monthByMonthLoading || loading}
              />

              {/* <MonthlyBooksStatusSection
                monthLabel={monthLabel}
                modules={data?.monthlyModuleStatus}
                loading={monthlyModuleStatusLoading || loading}
              /> */}
              <div className="flex w-full items-start justify-between gap-6">
                <div className="flex flex-col items-start gap-1">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    Accountant Work Summary
                  </span>
                  <span className="text-body font-body text-subtext-color">
                    See how your books are progressing, month by month.
                  </span>
                  {accountantSummary?.periodRange?.label && (
                    <span className="text-caption font-caption text-neutral-600">
                      {accountantSummary.periodRange.label}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <span className="text-body-bold font-body-bold text-subtext-color">
                    Viewing:
                  </span>
                  <SubframeCore.DropdownMenu.Root>
                    <SubframeCore.DropdownMenu.Trigger asChild={true}>
                      <Button
                        variant="neutral-secondary"
                        size="medium"
                        iconRight={<FeatherChevronDown />}
                        onClick={(
                          event: React.MouseEvent<HTMLButtonElement>,
                        ) => {}}
                      >
                        {summaryPeriod === "last_3_months"
                          ? "Last 3 Months"
                          : `${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`}
                      </Button>
                    </SubframeCore.DropdownMenu.Trigger>
                    <SubframeCore.DropdownMenu.Portal>
                      <SubframeCore.DropdownMenu.Content
                        side="bottom"
                        align="start"
                        sideOffset={4}
                        asChild={true}
                      >
                        <DropdownMenu>
                          <DropdownMenu.DropdownItem
                            icon={null}
                            onClick={() => {
                              onSummaryPeriodChange("last_3_months");
                            }}
                            className={
                              summaryPeriod === "last_3_months"
                                ? "bg-brand-50"
                                : ""
                            }
                          >
                            Last 3 Months
                          </DropdownMenu.DropdownItem>
                          <DropdownMenu.DropdownDivider />
                          {workHistory.map((item, idx) => {
                            const monthNumber = getMonthNumberFromAbbreviation(
                              item.month,
                            );
                            const isSelected =
                              selectedYear === item.year &&
                              selectedMonth === monthNumber;
                            return (
                              <DropdownMenu.DropdownItem
                                key={`${item.year}-${monthNumber}-${idx}`}
                                icon={null}
                                onClick={() => {
                                  onSummaryPeriodChange("this_month");
                                  onSummaryMonthChange(item.year, monthNumber);
                                }}
                                className={
                                  isSelected && summaryPeriod === "this_month"
                                    ? "bg-brand-50"
                                    : ""
                                }
                              >
                                {MONTH_NAMES[monthNumber - 1]} {item.year}
                              </DropdownMenu.DropdownItem>
                            );
                          })}
                        </DropdownMenu>
                      </SubframeCore.DropdownMenu.Content>
                    </SubframeCore.DropdownMenu.Portal>
                  </SubframeCore.DropdownMenu.Root>
                </div>
              </div>

              <AccountantSummaryCardsSection
                summary={accountantSummary}
                loading={accountantSummaryLoading}
                error={accountantSummaryError}
              />

              <AccountantMonthDrilldownSection
                detailYear={detailYear}
                detailMonth={detailMonth}
                detailPeriod={detailPeriod}
                workHistory={workHistory}
                onDetailMonthChange={onDetailMonthChange}
                onDetailPeriodChange={onDetailPeriodChange}
                getMonthNumberFromAbbreviation={getMonthNumberFromAbbreviation}
                billsDetail={billsDetail}
                billsDetailLoading={billsDetailLoading}
                billsDetailError={billsDetailError}
                billsOffset={billsOffset}
                billsLimit={billsLimit}
                onBillsPrevious={() =>
                  setBillsOffset((prev: number) =>
                    Math.max(0, prev - billsLimit),
                  )
                }
                onBillsNext={() =>
                  setBillsOffset((prev: number) => prev + billsLimit)
                }
                invoicesDetail={invoicesDetail}
                invoicesDetailLoading={invoicesDetailLoading}
                invoicesDetailError={invoicesDetailError}
                invoicesOffset={invoicesOffset}
                invoicesLimit={invoicesLimit}
                onInvoicesPrevious={() =>
                  setInvoicesOffset((prev: number) =>
                    Math.max(0, prev - invoicesLimit),
                  )
                }
                onInvoicesNext={() =>
                  setInvoicesOffset((prev: number) => prev + invoicesLimit)
                }
                transactionsDetail={transactionsDetail}
                transactionsDetailLoading={transactionsDetailLoading}
                transactionsDetailError={transactionsDetailError}
              />

              <div className="flex w-full items-start gap-6 bg-neutral-50">
                <RecentActivitySection
                  items={data?.recentActivity ?? undefined}
                  loading={recentActivityLoading}
                />
              </div>

              <WorkHistorySection
                history={workHistory}
                loading={workHistoryLoading}
                error={workHistoryError}
                onSelectMonth={onDetailMonthChange}
                getMonthNumberFromAbbreviation={getMonthNumberFromAbbreviation}
              />

              {/* <AccountantPerformanceInsightsSection
                insights={accountantPerformanceInsights}
                loading={accountantPerformanceInsightsLoading}
                error={accountantPerformanceInsightsError}
              /> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
